import React from "react";

import { Props } from "./types";
import styles from "./styles.scss";

function useDrag(
  move: (x: number, y: number) => void,
  done: () => void,
  enabled: boolean,
  deps: any[],
) {
  React.useEffect(() => {
    if (!enabled) return;

    const cancelSelection = () => done();
    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      const mouseEvent = e as MouseEvent;
      const touchEvent = e as TouchEvent;

      move(
        !!mouseEvent.clientX
          ? mouseEvent.clientX
          : touchEvent.changedTouches?.length > 0
            ? touchEvent.changedTouches[0].clientX
            : 0,
        !!mouseEvent.clientY
          ? mouseEvent.clientY
          : touchEvent.changedTouches?.length > 0
            ? touchEvent.changedTouches[0].clientY
            : 0,
      );
    };

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", cancelSelection);
    window.addEventListener("touchend", cancelSelection);

    return () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", cancelSelection);
      window.removeEventListener("touchend", cancelSelection);
    };
  }, [...deps, enabled]);
}

function Resizeable(props: Props) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const isDragging = React.useRef(false);
  const getNextSize = (x: number) => {
    if (!rootRef.current) return 0;
    const rootRect = rootRef.current.getBoundingClientRect();

    return Math.max(x - rootRect.x, 20);
  };

  useDrag(
    (x) => {
      if (isDragging.current && props.resize) {
        props.resize(getNextSize(x));
        document.body.classList.add(styles.grabbed);
      }
    },
    () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.classList.remove(styles.grabbed);
        props.done();
      }
    },
    !!props.resize,
    [props.done, props.resize],
  );

  return (
    <>
      {props.children(
        rootRef,
        <div
          className={styles.handle}
          onMouseDown={() => {
            isDragging.current = true;
          }}
          onTouchStart={() => {
            isDragging.current = true;
          }}
        />,
      )}
    </>
  );
}

export { Resizeable };
